import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ShieldCheckIcon as ShieldCheck, MagnifyingGlassIcon as MagnifyingGlass, CircleNotchIcon as CircleNotch, CheckCircleIcon as CheckCircle } from "@phosphor-icons/react";

export function SophosWizard({ open, setOpen, onImported }) {
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [sel, setSel] = useState({});
  const [q, setQ] = useState("");
  const [hideImported, setHideImported] = useState(true);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (!open) { setCustomers([]); setSel({}); setQ(""); return; }
    setLoading(true);
    api.get("/sophos/customers")
      .then(({ data }) => setCustomers(data.customers))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore Sophos"))
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = customers.filter((c) =>
    (!hideImported || !c.gia_importato) &&
    (!q || c.ragione_sociale.toLowerCase().includes(q.toLowerCase()) || (c.citta || "").toLowerCase().includes(q.toLowerCase()))
  );
  const selectable = filtered.filter((c) => !c.gia_importato);
  const selCount = Object.values(sel).filter(Boolean).length;
  const allSelected = selectable.length > 0 && selectable.every((c) => sel[c.sophos_id]);

  const toggleAll = () => {
    const next = { ...sel };
    if (allSelected) selectable.forEach((c) => { next[c.sophos_id] = false; });
    else selectable.forEach((c) => { next[c.sophos_id] = true; });
    setSel(next);
  };

  const doImport = async () => {
    const ids = Object.entries(sel).filter(([, v]) => v).map(([k]) => k);
    if (ids.length === 0) { toast.error("Seleziona almeno un cliente"); return; }
    setImporting(true);
    try {
      const { data } = await api.post("/sophos/import", { sophos_ids: ids });
      toast.success("Import completato", { description: `${data.creati} clienti creati, ${data.saltati} saltati` });
      setOpen(false);
      onImported?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="rounded-sm max-w-3xl max-h-[92vh] flex flex-col" data-testid="sophos-wizard">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><ShieldCheck size={20} className="text-primary" /> Importa clienti da Sophos Central</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">Seleziona i tenant Sophos da importare nell'anagrafica clienti. I clienti già presenti (per nome o ID Sophos) sono contrassegnati e non riselezionabili.</p>

        <div className="flex items-center gap-3 py-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="sophos-search" placeholder="Cerca cliente o città..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 rounded-sm" />
          </div>
          <label className="flex items-center gap-2 text-sm whitespace-nowrap cursor-pointer">
            <Checkbox checked={hideImported} onCheckedChange={setHideImported} data-testid="hide-imported" /> Nascondi già importati
          </label>
        </div>

        <div className="flex-1 overflow-y-auto border border-border rounded-sm">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <CircleNotch size={28} className="animate-spin mb-2" /> Caricamento clienti Sophos...
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-secondary sticky top-0">
                <tr>
                  <th className="w-10 p-2"><Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="sophos-select-all" /></th>
                  <th className="text-left p-2 font-medium">Cliente</th>
                  <th className="text-left p-2 font-medium">Città</th>
                  <th className="text-left p-2 font-medium">Regione</th>
                  <th className="text-left p-2 font-medium">Stato</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && <tr><td colSpan={5} className="text-center text-muted-foreground py-10">Nessun cliente da mostrare.</td></tr>}
                {filtered.map((c) => (
                  <tr key={c.sophos_id} data-testid="sophos-cust-row" className="border-t border-border hover:bg-secondary/50">
                    <td className="p-2">
                      <Checkbox disabled={c.gia_importato} checked={!!sel[c.sophos_id]} onCheckedChange={(v) => setSel((s) => ({ ...s, [c.sophos_id]: v }))} data-testid="sophos-cust-check" />
                    </td>
                    <td className="p-2 font-medium">
                      {c.ragione_sociale}
                      {c.gia_importato && <Badge variant="outline" className="rounded-sm text-[10px] ml-2 text-green-700"><CheckCircle size={11} className="mr-0.5" /> già importato</Badge>}
                    </td>
                    <td className="p-2 text-muted-foreground">{c.citta || "—"}</td>
                    <td className="p-2 text-muted-foreground uppercase text-xs">{c.regione || "—"}</td>
                    <td className="p-2 text-muted-foreground">{c.status || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <DialogFooter className="items-center">
          <span className="text-sm text-muted-foreground mr-auto">{selCount} selezionati · {customers.length} totali</span>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Annulla</Button>
          <Button data-testid="sophos-import-btn" onClick={doImport} disabled={importing || selCount === 0} className="rounded-sm">
            {importing ? "Importazione..." : `Importa ${selCount} clienti`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
